import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import { ArrowLeft } from "lucide-react";

export default function CustomerRegisterPage() {
  const { registerCustomer } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const backTo = params.get("redirect") || "/";
  const [form, setForm] = useState({
    name: "", surname: "", email: "", password: "", phone: "",
    address: "", city: "", cap: "", country: "Italia",
    consenso_marketing: false, accepted_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepted_terms) { setError("Devi accettare i Termini e la Privacy Policy"); return; }
    setError(""); setLoading(true);
    const res = await registerCustomer(form);
    setLoading(false);
    if (!res.success) { setError(res.error); toast.error(res.error); return; }
    toast.success("Account creato!");
    navigate(backTo);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="customer-register-page">
      <div className="w-full max-w-2xl">
        <Link to={backTo} data-testid="cr-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Indietro
        </Link>
        <h1 className="editorial-h1 text-4xl lg:text-5xl mb-2">Crea il tuo account</h1>
        <p className="text-muted-foreground mb-8">Registrandoti potrai monitorare ordini, tracking e ricevere offerte dai tuoi rivenditori preferiti.</p>
        <form onSubmit={submit} className="bg-white border border-border rounded-sm p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input k="name" label="Nome*" value={form.name} setForm={setForm} form={form} required />
            <Input k="surname" label="Cognome*" value={form.surname} setForm={setForm} form={form} required />
            <Input k="email" label="Email*" type="email" value={form.email} setForm={setForm} form={form} required colSpan={2} />
            <Input k="password" label="Password*" type="password" value={form.password} setForm={setForm} form={form} required />
            <Input k="phone" label="Telefono*" value={form.phone} setForm={setForm} form={form} required />
            <Input k="address" label="Indirizzo*" value={form.address} setForm={setForm} form={form} required colSpan={2} />
            <Input k="city" label="Città*" value={form.city} setForm={setForm} form={form} required />
            <Input k="cap" label="CAP*" value={form.cap} setForm={setForm} form={form} required />
            <Input k="country" label="Paese" value={form.country} setForm={setForm} form={form} />
          </div>
          <div className="border-t border-border pt-4 space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer text-sm">
              <Checkbox data-testid="cr-marketing" checked={form.consenso_marketing}
                onCheckedChange={(v) => setForm({ ...form, consenso_marketing: !!v })} />
              <span className="text-xs leading-relaxed">Acconsento a ricevere comunicazioni di marketing su offerte e novità (facoltativo).</span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer text-sm">
              <Checkbox data-testid="cr-terms" checked={form.accepted_terms}
                onCheckedChange={(v) => setForm({ ...form, accepted_terms: !!v })} required />
              <span className="text-xs leading-relaxed">Accetto i Termini & Condizioni e la Privacy Policy <span className="text-brand">(obbligatorio)</span></span>
            </label>
          </div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3">{error}</div>}
          <button type="submit" disabled={loading} data-testid="cr-submit"
            className="w-full h-12 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium disabled:opacity-50">
            {loading ? "..." : "Crea account"}
          </button>
          <p className="text-xs text-muted-foreground text-center">
            Hai già un account? <Link to="/login" className="text-brand hover:underline">Accedi</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

const Input = ({ k, label, value, setForm, form, type = "text", required, colSpan = 1 }) => {
  const [show, setShow] = React.useState(false);
  const isPwd = type === "password";
  const effectiveType = isPwd && show ? "text" : type;
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="overline text-muted-foreground mb-1 block">{label}</label>
      <div className="relative">
        <input data-testid={`cr-${k}`} type={effectiveType} value={value} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          required={required}
          className={`w-full h-10 px-3 ${isPwd ? "pr-10" : ""} rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-sm`} />
        {isPwd && (
          <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1}
            aria-label={show ? "Nascondi password" : "Mostra password"}
            data-testid={`cr-${k}-toggle`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
};
